import { useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";

const HOME_PATHS = ["/", "/home"];
const EXIT_CONFIRM_MS = 2000;

function isMobileViewport() {
  if (typeof window === "undefined") return false;
  return (
    window.innerWidth <= 768 ||
    window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function isHomePath(pathname) {
  return HOME_PATHS.includes(pathname);
}

function pushOverlayGuard(kind) {
  try {
    window.history.pushState(
      { ...(window.history.state || {}), mobileOverlayGuard: kind },
      "",
      window.location.href
    );
  } catch (e) {
    // ignore
  }
}

/**
 * Mobile back handling:
 * - Create post / messenger open → back closes overlay (does not leave the page)
 * - Other pages → browser/React Router back works normally (previous page)
 * - Home → first back stays on home; second back within 2s exits the app
 */
export default function MobileBackHandler({
  isCreatePostOpen = false,
  onCloseCreatePost,
  isMessengerOpen = false,
}) {
  const location = useLocation();
  const { user } = useSelector((state) => state);
  const lastBackPressRef = useRef(0);
  const allowExitRef = useRef(false);
  const overlayOpenRef = useRef(false);
  const createPostOpenRef = useRef(isCreatePostOpen);
  const messengerOpenRef = useRef(isMessengerOpen);
  const pathnameRef = useRef(location.pathname);

  createPostOpenRef.current = isCreatePostOpen;
  messengerOpenRef.current = isMessengerOpen;
  pathnameRef.current = location.pathname;

  const isMessengerVisible = useCallback(
    () =>
      messengerOpenRef.current ||
      window.__messengerSidebarOpen ||
      window.__messengerChatOpen,
    []
  );

  // When an overlay opens, add one history entry so Android/browser back
  // closes the overlay instead of leaving the current page.
  useEffect(() => {
    if (!isMobileViewport() || !user?.token) return;

    const overlayOpen = isCreatePostOpen || isMessengerVisible();
    if (overlayOpen && !overlayOpenRef.current) {
      pushOverlayGuard(isCreatePostOpen ? "createPost" : "messenger");
    }
    overlayOpenRef.current = overlayOpen;
  }, [isCreatePostOpen, isMessengerOpen, user?.token, isMessengerVisible]);

  // Home-only exit guard (do not push guards on other routes — that broke back nav)
  useEffect(() => {
    if (!isMobileViewport() || !user?.token) return;
    if (!isHomePath(location.pathname)) return;
    if (isCreatePostOpen || isMessengerVisible()) return;

    pushOverlayGuard("homeExit");
    allowExitRef.current = false;
    lastBackPressRef.current = 0;
  }, [location.pathname, user?.token, isCreatePostOpen, isMessengerOpen, isMessengerVisible]);

  useEffect(() => {
    if (!isMobileViewport() || !user?.token) {
      return undefined;
    }

    const onPopState = () => {
      if (allowExitRef.current) {
        allowExitRef.current = false;
        return;
      }

      // 1) Close overlays first
      if (createPostOpenRef.current) {
        onCloseCreatePost?.();
        return;
      }

      if (isMessengerVisible()) {
        window.dispatchEvent(new CustomEvent("messengerHardwareBack"));
        return;
      }

      // 2) Non-home routes: do not intercept — React Router already handled back
      if (!isHomePath(pathnameRef.current)) {
        return;
      }

      // 3) Home: require double-back to exit
      const now = Date.now();
      if (now - lastBackPressRef.current < EXIT_CONFIRM_MS) {
        allowExitRef.current = true;
        lastBackPressRef.current = 0;
        window.history.back();
        return;
      }

      lastBackPressRef.current = now;
      pushOverlayGuard("homeExit");
    };

    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [user?.token, onCloseCreatePost, isMessengerVisible]);

  return null;
}
